import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextAreaAutosize from 'components/common/TextareaAutosize'
import GrinChip from 'components/common/GrinChip'
import { useCreateNoteMutation } from 'store/modules/PatientNotes'
import { trackEvent } from 'utils/analyticsUtils'
import { mapToCreateNoteInput } from 'utils/mappers/noteMappers'
import useShowSnackbar from 'hooks/useShowSnackbar'
import { UserNoteVisiblities } from 'logic/patientNotesLogic'
import GrinButton from 'components/common/buttons/GrinButton/GrinButton'
import NoteVisibilityMenuItem from './NoteVisibilityMenuItem'
import useNoteVisibilityOptions from './useNoteVisibilityOptions'
import InputIcon from './icons/InputIcon'
import { NOTE_INPUT_ID } from './notesConsts'

const REPLY_TAG_HEIGHT = 32

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    marginTop: 10
  },
  inputContainer: {
    position: 'relative',
    flex: 1,
    height: ({ isReplying }) => (isReplying ? 100 + REPLY_TAG_HEIGHT : 100),
    borderRadius: 10,
    border: '1px solid var(--text-color-4)',
    overflow: 'hidden',
    padding: '4px 20px',
    backgroundColor: ({ isLoading }) => (isLoading ? 'var(--bg-color-2)' : 'var(--bg-color-10)')
  },
  replyTagContainer: {
    height: REPLY_TAG_HEIGHT,
    display: 'flex',
    alignItems: 'center'
  },
  noteInput: {
    height: 100,
    border: 'none!important',
    fontSize: '14px !important'
  },
  addButtonContainer: {
    position: 'absolute',
    right: 5,
    bottom: 5
  },
  addButton: {
    width: 'auto !important',
    height: 32
  },
  addButtonIconContainer: {
    margin: 0
  }
}))

const NoteInput = ({ needsAttentionActions }) => {
  const { t } = useTranslation()

  const [noteContent, setNoteContent] = useState('')

  const [createNote, { isLoading: isCreatingNote, isError: isFailedCreatingNote }] = useCreateNoteMutation()
  useShowSnackbar({
    isOpen: isFailedCreatingNote,
    message: t('pages.patients.patientSwitchableView.notes.createNoteError')
  })

  const isReplying = !!needsAttentionActions?.isReplying
  const isLoading = isCreatingNote || !!needsAttentionActions?.isSaving

  const handleAddNote = useCallback(
    async (visibility = UserNoteVisiblities.Public) => {
      // The resolve endpoint writes the reply note itself, so a reply never creates a note here.
      if (isReplying) {
        needsAttentionActions.submitReply({
          note: noteContent.trim(),
          onDone: ({ isSuccess }) => isSuccess && setNoteContent('')
        })
        return
      }

      const payload = mapToCreateNoteInput({
        noteContent: noteContent.trim(),
        visibility
      })

      trackEvent('Patient notes - Note added', payload)
      await createNote({ variables: payload })

      setNoteContent('')
    },
    [createNote, noteContent, isReplying, needsAttentionActions]
  )

  const { visibilityOptions } = useNoteVisibilityOptions({ onVisibilitySelected: handleAddNote })

  const classes = useStyles({ isLoading, isReplying })
  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        {isReplying && (
          <div className={classes.replyTagContainer}>
            <GrinChip
              text={t('pages.patients.patientSwitchableView.notes.replyToNeedsAttention')}
              backgroundColor="rgb(255, 242, 215)"
              textColor="rgb(219, 97, 93)"
              onDelete={needsAttentionActions.onReplyTagRemoved}
              automationsPrefix="add-note-reply"
            />
          </div>
        )}
        <TextAreaAutosize
          rowsMax={4}
          style={{ bright: true }}
          className={classes.noteInput}
          placeholder={t('pages.patients.patientSwitchableView.notes.addNotePlaceholder')}
          keepErrorContainerWhenInactive={false}
          value={noteContent}
          isDisabled={isLoading}
          setValue={setNoteContent}
          id={NOTE_INPUT_ID}
        />
        <div className={classes.addButtonContainer}>
          <GrinButton
            icon={<InputIcon style={{ marginRight: 8 }} />}
            label={t('pages.patients.patientSwitchableView.notes.addNote')}
            onClick={() => handleAddNote(UserNoteVisiblities.Public)}
            isLoading={isLoading}
            disabled={!noteContent.trim()}
            id="add-note-button"
            className={classes.addButton}
            iconContainerClassName={classes.addButtonIconContainer}
            OptionMenuItemComponent={NoteVisibilityMenuItem}
            options={isReplying ? [] : visibilityOptions}
          />
        </div>
      </div>
    </div>
  )
}

export default NoteInput
